import { HelpCircle } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { useTranslation } from "react-i18next";

interface StatusFieldsProps {
  profile: any;
  setProfile: (profile: any) => void;
  readOnly: boolean;
  loading: boolean;
}

export function StatusFields({
  profile,
  setProfile,
  readOnly,
  loading
}: StatusFieldsProps) {
  const { t } = useTranslation(["talent", "common"]);

  return (
    <>
      <div className="space-y-1">
        <label className="text-sm font-medium">
          {t("forms.sections.personal_data.fields.relationship_status.label")}
        </label>
        <Select
          disabled={readOnly}
          value={profile?.relationship_status}
          onValueChange={(value) =>
            setProfile((prev: any) => ({
              ...prev,
              relationship_status: value
            }))
          }
        >
          <SelectTrigger loading={loading}>
            <SelectValue placeholder={t("forms.common.placeholders.select")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="single">
              {t("forms.common.relationship_status.single")}
            </SelectItem>
            <SelectItem value="married">
              {t("forms.common.relationship_status.married")}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1">
        <div className="flex items-center gap-1">
          <label className="text-sm font-medium">
            {t("forms.sections.personal_data.fields.family_reunion.label")}
          </label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <HelpCircle className="h-4 w-4 text-gray-400" />
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {t("forms.sections.personal_data.fields.family_reunion.help")}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Select
          disabled={readOnly}
          value={profile?.family_reunion ? "yes" : "no"}
          onValueChange={(value) =>
            setProfile((prev: any) => ({
              ...prev,
              family_reunion: value === "yes"
            }))
          }
        >
          <SelectTrigger loading={loading}>
            <SelectValue placeholder={t("forms.common.placeholders.select")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="yes">{t("forms.common.boolean.yes")}</SelectItem>
            <SelectItem value="no">{t("forms.common.boolean.no")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1">
        <div className="flex items-center gap-1">
          <label className="text-sm font-medium">
            {t("forms.sections.personal_data.fields.criminal_record.label")}
          </label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <HelpCircle className="h-4 w-4 text-gray-400" />
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {t(
                    "forms.sections.personal_data.fields.criminal_record.help"
                  )}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Select
          disabled={readOnly}
          value={profile?.criminal_record}
          onValueChange={(value) =>
            setProfile((prev: any) => ({ ...prev, criminal_record: value }))
          }
        >
          <SelectTrigger loading={loading}>
            <SelectValue placeholder={t("forms.common.placeholders.select")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="yes">{t("forms.common.boolean.yes")}</SelectItem>
            <SelectItem value="no">{t("forms.common.boolean.no")}</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </>
  );
}
